/**
 * Data access for a planning poker room. Pure functions over the PocketBase SDK; React Query
 * hooks and the realtime wiring live in src/hooks/use-room.ts.
 *
 * Plain records (rooms, players, issues) use the collections directly. Game actions change
 * several records at once and run server-side (server/pb_hooks/poker.pb.js).
 */
import { ClientResponseError, type RecordSubscription } from "pocketbase";
import type { IssuesResponse, PlayersResponse, RoomsResponse, VotesResponse } from "@/lib/pb-types";
import { pb } from "@/lib/pocketbase";
import type { VotingSystem } from "@/lib/poker";

export type Room = RoomsResponse;
export type Player = PlayersResponse;
export type Issue = IssuesResponse<Record<string, string>>;
export type Vote = VotesResponse;

export type RoomSnapshot = {
  room: Room;
  /** Empty until you joined: the API only shows a room's players to its players. */
  players: Player[];
  issues: Issue[];
  /** Your own vote while voting, everyone's once the cards are revealed. */
  votes: Vote[];
};

export type RoomSettings = {
  name: string;
  voting_system: VotingSystem;
  who_can_reveal: "all" | "admin";
  who_can_manage_issues: "all" | "admin";
  auto_reveal: boolean;
  show_average: boolean;
  show_countdown: boolean;
};

export async function createRoom(settings: RoomSettings, adminId: string): Promise<Room> {
  return pb.collection("rooms").create<Room>({ ...settings, state: "voting", admin: adminId });
}

const inRoom = (roomId: string) => pb.filter("room = {:roomId}", { roomId });

/** Everything the room page shows, or null when the room does not exist. */
export async function getRoomSnapshot(roomId: string): Promise<RoomSnapshot | null> {
  let room: Room;
  try {
    room = await pb.collection("rooms").getOne<Room>(roomId);
  } catch (error) {
    if (error instanceof ClientResponseError && error.status === 404) return null;
    throw error;
  }
  const filter = inRoom(roomId);
  const [players, issues, votes] = await Promise.all([
    pb.collection("players").getFullList<Player>({ filter, sort: "created" }),
    pb.collection("issues").getFullList<Issue>({ filter, sort: "created" }),
    pb.collection("votes").getFullList<Vote>({ filter }),
  ]);
  return { room, players, issues, votes };
}

export async function joinRoom(roomId: string, userId: string, name: string): Promise<Player> {
  return pb.collection("players").create<Player>({
    room: roomId,
    user: userId,
    name,
    has_voted: false,
    active: true,
  });
}

/** Heartbeat: marks the player present (the server times out players that stop sending it). */
export async function markPresent(playerId: string): Promise<void> {
  await pb.collection("players").update(playerId, { active: true });
}

/** Marks the player as gone while the page unloads (`keepalive` outlives the tab). */
export function markLeaving(playerId: string): void {
  void fetch(pb.buildURL(`/api/collections/players/records/${encodeURIComponent(playerId)}`), {
    method: "PATCH",
    keepalive: true,
    headers: { "Content-Type": "application/json", Authorization: pb.authStore.token },
    body: JSON.stringify({ active: false }),
  }).catch(() => undefined);
}

const action = (roomId: string, path: string, body?: object) =>
  pb.send(`/api/poker/rooms/${encodeURIComponent(roomId)}/${path}`, { method: "POST", body });

/** Picks a card; an empty value takes it back. */
export async function castVote(roomId: string, value: string): Promise<void> {
  await action(roomId, "vote", { value });
}

export async function revealCards(roomId: string): Promise<void> {
  await action(roomId, "reveal");
}

export async function startNewRound(roomId: string): Promise<void> {
  await action(roomId, "new-round");
}

/** Makes `issueId` the one being estimated and starts a fresh round for it. */
export async function startIssue(roomId: string, issueId: string): Promise<void> {
  await action(roomId, `issues/${encodeURIComponent(issueId)}/start`);
}

export async function addIssue(roomId: string, title: string): Promise<Issue> {
  return pb.collection("issues").create<Issue>({ room: roomId, title, status: "pending" });
}

export async function deleteIssue(issueId: string): Promise<void> {
  await pb.collection("issues").delete(issueId);
}

/** Applies a realtime create/update/delete to a list of records. */
export function applyRealtime<T extends { id: string }>(
  list: T[],
  { action: kind, record }: RecordSubscription<T>,
): T[] {
  if (kind === "delete") return list.filter((item) => item.id !== record.id);
  if (!list.some((item) => item.id === record.id)) return [...list, record];
  return list.map((item) => (item.id === record.id ? record : item));
}
